import classnames from 'classnames'
import PropTypes from 'prop-types'

import {Map} from 'immutable'
import {useState} from 'react'

import IconButton from '@mui/material/IconButton'

import DownloadIcon from '../../../DownloadIcon'
import PauseButtonIcon from '../../../audio_player/PauseButtonIcon'
import PlayButtonIcon from '../../../audio_player/PlayButtonIcon'
import PreviewIcon from './PreviewIcon'
import FileIcon from './FileIcon'
import RemoveFile from './RemoveFile'
import {useAudioPlayer} from '../../../audio_player/AudioPlayerContext'
import {isPlayableAudioFile} from '../../../audio_player/audioUtils'

import FileUploadActions from '../../../../shared_actions/FileUploadActions'
import xIcon from '../../../../shared_assets/v2/close.svg'
import ImageViewerController from '../../../ImageViewerController'
import PdfViewerController from '../../../PdfViewerController'
import spinnerIconBlack from '../../../../pro/assets/images/spinner-icon-black.gif'
import Logger from '../../../../lib/NewLogger'
import MixpanelLogger from '../../../../lib/MixpanelLogger'

import {fileExtension} from '../lib/tools'

import './fileView.scss'

const PREVIEWABLE_IMAGE_EXTENSIONS = [
  'bmp',
  'gif',
  'heic',
  'jpeg',
  'jpg',
  'png',
  'svg',
  'tif',
  'tiff'
]

const FileView = ({
  actor,
  context,
  deleted,
  elementId,
  error,
  eventType,
  itemResponse,
  id,
  isViewMode,
  itemName,
  loaded,
  markFileAsDeleted,
  name,
  pdfPreviewEventName = 'click_image_preview',
  stopFileUpload,
  total,
  uploadStatus
}) => {
  const extension = (fileExtension(name || '') || '').toLowerCase()
  const audioPlayer = useAudioPlayer()

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false)

  const isAudio = isPlayableAudioFile(extension)
  const isCurrentTrack = audioPlayer && audioPlayer.currentTrack && audioPlayer.currentTrack.id === id
  const isTrackPlaying = isCurrentTrack && audioPlayer.isPlaying

  const getPreviewType = () => {
    if (!extension) return null

    if (extension === 'pdf') return 'pdf'

    if (PREVIEWABLE_IMAGE_EXTENSIONS.includes(extension)) return 'image'

    return null
  }

  const trackMixpanelEvent = (mixpanelEventName, payload = {}) => {
    MixpanelLogger.track(mixpanelEventName, {
      context,
      file_type: extension,
      item: itemName,
      ...payload
    })
  }

  const handleDownload = event => {
    if (event) event.stopPropagation()

    trackMixpanelEvent('click_attachment_download')

    Logger.log({
      name: 'clicked_download_attachment',
      payload: {
        actor,
        context,
        element_id: elementId,
        everplan_id: itemResponse.get('everplan-id'),
        item: itemName,
        type: eventType,
        wildcard: elementId
      }
    })

    FileUploadActions.downloadFile(id, name)
  }

  const handleClosePreviewModal = () => setIsPreviewModalOpen(false)

  const handlePlayAudio = event => {
    if (event) event.stopPropagation()

    if (!isTrackPlaying) trackMixpanelEvent('click_audio_play')

    if (audioPlayer) audioPlayer.playTrack(id, name)
  }

  const handleOpenPreview = event => {
    if (event) event.stopPropagation()

    const previewType = getPreviewType()

    trackMixpanelEvent(previewType === 'pdf' ? pdfPreviewEventName : 'click_image_preview', {preview_type: previewType})
    setIsPreviewModalOpen(true)
  }

  const handleFileClick = () => {
    if (isViewMode && isAudio && audioPlayer) {
      handlePlayAudio()
      return
    }

    if (getPreviewType())
      handleOpenPreview()
    else
      handleDownload()
  }

  const handleStopFileUpload = () => stopFileUpload(id)

  const isEncryptingOrEncrypted = !uploadStatus || uploadStatus === 'encrypted' || uploadStatus === 'encrypting'

  const fileUploadStatus = () => {
    if (isEncryptingOrEncrypted || uploadStatus === 'Virus Detected')
      return ''
    else
      return uploadStatus
  }

  const previewType = getPreviewType()
  const virusFound = uploadStatus === 'Virus Detected'
  const complete = (!error && (loaded === total))
  const uploading = uploadStatus === 'Uploading'
  const scanning = uploadStatus === 'Scanning for virus'
  const finishingUpload = uploadStatus === 'Finishing Upload'

  const fileErrorText = () => {
    if (error)
      return error
    if (virusFound)
      return 'Upload failed: virus detected. Please check your email for details.'
  }

  const renderViewModeActions = () => {
    if (!isViewMode || !isEncryptingOrEncrypted) return null

    return (
      <div className='file-actions'>
        {
          isAudio && (
            <IconButton
              aria-label={isTrackPlaying ? `Pause ${name}` : `Play ${name}`}
              className='file-action-button'
              onClick={handlePlayAudio}>
              {isTrackPlaying ? <PauseButtonIcon /> : <PlayButtonIcon />}
            </IconButton>
          )
        }
        {
          previewType && (
            <IconButton
              aria-label={`Preview ${name}`}
              className='file-action-button'
              onClick={handleOpenPreview}>
              <PreviewIcon height='17' width='19' />
            </IconButton>
          )
        }
        <IconButton
          aria-label={`Download ${name}`}
          className='file-action-button'
          onClick={handleDownload}>
          <DownloadIcon height='16' stroke='currentColor' width='16' />
        </IconButton>
      </div>
    )
  }

  // hide file that has been marked for deletion.
  if (deleted) return <noscript />

  return (
    <div
      className={classnames('file-view', 'flex-container', {'is-playing': isCurrentTrack})}
      data-testid={`file-${id}`}>
      <FileIcon fileName={name} />
      <div className='flex-child'>
        <div className='file-details-container flex-container'>
          <div className='file-details flex-child'>
            <div className='file-name'>
              {
                isViewMode && isEncryptingOrEncrypted ?
                  <button
                    aria-label={`View file ${name}`}
                    className='file-name-button'
                    onClick={handleFileClick}
                    type='button'>
                    {name}
                  </button> :
                  <span role='text' aria-label={`File name: ${name}`}>
                    {name}
                  </span>
              }
              {uploading &&
              <img
                alt={`Remove ${name}`}
                className='xfile-icon'
                onClick={handleStopFileUpload}
                src={xIcon}
              />}
            </div>
            {
              id && isPreviewModalOpen && previewType === 'pdf' &&
              <PdfViewerController
                isModalOpen={isPreviewModalOpen}
                onCloseModal={handleClosePreviewModal}
                onDownloadClick={handleDownload}
                pdfName={name}
                uploadId={id}
              />
            }
            {
              id && isPreviewModalOpen && previewType === 'image' &&
              <ImageViewerController
                fileExtension={extension}
                imageName={name}
                isModalOpen={isPreviewModalOpen}
                onCloseModal={handleClosePreviewModal}
                onDownloadClick={handleDownload}
                uploadId={id}
              />
            }
            {!error && !complete && <progress className={classnames({complete}, 'progress-bar')} max={total} value={error ? 0 : loaded} /> }
            <div className='upload-status'>
              {(finishingUpload || scanning) && <img alt='spinner icon' src={spinnerIconBlack} className='encryption-spinner' />}
              {fileUploadStatus()}
            </div>
            {(error || virusFound) && <div className='error-description'>{fileErrorText()}</div>}
          </div>
          {renderViewModeActions()}
          {
            (isEncryptingOrEncrypted || virusFound) &&
                <RemoveFile
                  removeFile={markFileAsDeleted}
                  isViewMode={isViewMode}
                  id={id}
                  name={name}
                  virusFound={virusFound}
                />
          }
        </div>
      </div>
    </div>
  )
}

FileView.propTypes = {
  actor: PropTypes.string,
  context: PropTypes.string,
  deleted: PropTypes.bool,
  elementId: PropTypes.string,
  error: PropTypes.string,
  eventType: PropTypes.string,
  itemResponse: PropTypes.instanceOf(Map),
  id: PropTypes.string,
  isViewMode: PropTypes.bool,
  itemName: PropTypes.string,
  loaded: PropTypes.number,
  markFileAsDeleted: PropTypes.func,
  name: PropTypes.string,
  pdfPreviewEventName: PropTypes.string,
  stopFileUpload: PropTypes.func,
  total: PropTypes.number,
  uploadStatus: PropTypes.string
}

export default FileView
